import { handleResponse, handleError, headerset } from "../apiUtils";
import { convertToDateTime } from "components/common/dateUtils/convertToDateTime";
import {OrdineLavoro} from "../../components/main/produzione/ordiniLavoro/ordiniLavoroTypes";

// ============================================
// BASE URL
// ============================================

const baseUrl = (previsioneId: number, ordineLavoroId?: number | null) => {
    let url: string = `${process.env.API_URL}/api/pianificazione/previsioni/${previsioneId}/ordini_di_lavoro/`;

    if (ordineLavoroId) {
        url += `${ordineLavoroId}/`;
    }

    return url;
};

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Fetch tutti gli ordiniLavoro di una previsione
 * Non usiamo filtri/paginazione per questa vista inline
 */
export function fetchOrdiniLavoroByPrevisione(previsioneId: number) {
    const url = baseUrl(previsioneId);

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Get singolo ordineLavoro by ID
 */
export function getOrdiniLavoro(previsioneId: number, ordineLavoroId: number) {
    const url = baseUrl(previsioneId, ordineLavoroId);

    return fetch(url, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Save (create/update) ordineLavoro
 */
export function saveOrdiniLavoro(
    previsioneId: number,
    ordineLavoroToSave: OrdineLavoro,
    azione?: string
) {
    let url = baseUrl(previsioneId, ordineLavoroToSave.id);

    // Aggiungi azione come query parameter se specificata
    if (azione) {
        url += `?azione=${azione}`;
    }

    // Prepara i dati per l'invio
    const ordineLavoroData = {
        ...ordineLavoroToSave,
        // Converti le date in formato ISO per il backend
        data_arrivo_prevista: ordineLavoroToSave.data_arrivo_prevista
            ? convertToDateTime(ordineLavoroToSave.data_arrivo_prevista)
                ?.setLocale("it")
                .toFormat("yyyy-MM-dd")
            : null,
        data_consegna_prevista: ordineLavoroToSave.data_consegna_prevista
            ? convertToDateTime(ordineLavoroToSave.data_consegna_prevista)
                ?.setLocale("it")
                .toFormat("yyyy-MM-dd")
            : null,
        data_ricevimento: ordineLavoroToSave.data_ricevimento
            ? convertToDateTime(ordineLavoroToSave.data_ricevimento)
                ?.setLocale("it")
                .toFormat("yyyy-MM-dd")
            : null,
        // Assicurati che le quantità siano numeri
        qta_prevista: ordineLavoroToSave.qta_prevista
            ? Number(ordineLavoroToSave.qta_prevista)
            : null,
        qta_arrivata: ordineLavoroToSave.qta_arrivata
            ? Number(ordineLavoroToSave.qta_arrivata)
            : null,
    };

    return fetch(url, {
        method: ordineLavoroToSave.id ? "PUT" : "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
        body: JSON.stringify(ordineLavoroData),
    })
        .then(handleResponse)
        .catch(handleError);
}

/**
 * Delete ordineLavoro
 */
export function deleteOrdiniLavoro(previsioneId: number, ordineLavoroToDelete: OrdineLavoro) {
    const url = baseUrl(previsioneId, ordineLavoroToDelete.id);

    return fetch(url, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: headerset().Authorization,
        },
    })
        .then(handleResponse)
        .catch(handleError);
}

// ============================================
// FUNZIONI API AGGIUNTIVE PER AZIONI SPECIALI
// ============================================

/**
 * Crea ordineLavoro vuoto per una previsione
 * Utility per inizializzare un nuovo ordineLavoro con defaults
 */
export function createOrdiniLavoroVuoto(previsioneId: number): OrdineLavoro {
    return {
        previsione: previsioneId,
        stato: 'CONF',  // Stato default per nuovo ordineLavoro
        qta_prevista: undefined,
        data_arrivo_prevista: undefined,
        data_consegna_prevista: undefined,
        qta_arrivata: undefined,
        data_ricevimento: undefined,
        note: '',
    };
}